import { RefObject, useEffect } from 'react'

interface Args {
  containerRef: RefObject<HTMLElement>
  enabled: boolean
  totalRows: number
  hasAnswer: boolean
  onSkip: () => void
  onSubmit: () => void
  onActivate: () => void
  onMoveFocus: (updater: (current: number) => number) => void
}

// Window-level keydown so shortcuts work regardless of which child currently
// holds focus. Skips when focus is outside the form (e.g. the chat input
// below) and lets Enter/arrows pass through when the user is typing in the
// custom-answer input.
export const useClarificationKeyboard = ({
  containerRef,
  enabled,
  totalRows,
  hasAnswer,
  onSkip,
  onSubmit,
  onActivate,
  onMoveFocus,
}: Args) => {
  useEffect(() => {
    if (!enabled) return
    const handler = (e: KeyboardEvent) => {
      const container = containerRef.current
      if (!container) return
      const active = document.activeElement
      const isInForm =
        !active || active === document.body || container.contains(active)
      if (!isInForm) return

      const tag = (active as HTMLElement | null)?.tagName
      const inEditableField = tag === 'INPUT' || tag === 'TEXTAREA'

      if (e.key === 'Escape') {
        e.preventDefault()
        onSkip()
        return
      }
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault()
        if (hasAnswer) onSubmit()
        return
      }
      if (inEditableField) return
      if (e.key === 'Enter') {
        e.preventDefault()
        onActivate()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        onMoveFocus(i => Math.min(totalRows - 1, i + 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        onMoveFocus(i => Math.max(0, i - 1))
        return
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    containerRef,
    enabled,
    totalRows,
    hasAnswer,
    onSkip,
    onSubmit,
    onActivate,
    onMoveFocus,
  ])
}
